package co.uk.paxes.pinpoint.util

import kotlin.random.Random

/**
 * Seeded random number generator that produces deterministic results
 * across all Kotlin Multiplatform targets (JVM, JS, Native).
 * 
 * Uses a custom implementation to ensure cross-platform consistency.
 * 
 * Requirements: 3.3, 3.4, 10.4
 */
class SeededRandom(seed: Long) {
    // Use a simple Linear Congruential Generator for cross-platform consistency
    // Parameters from Numerical Recipes
    private var state: Long = seed
    
    private fun nextBits(): Long {
        state = (state * 6364136223846793005L + 1442695040888963407L)
        return state
    }
    
    /**
     * Returns the next random integer in the range [0, bound).
     */
    fun nextInt(bound: Int): Int {
        require(bound > 0) { "bound must be positive" }
        val bits = (nextBits() ushr 33).toInt()
        return (bits.toLong() * bound / Int.MAX_VALUE).toInt().coerceIn(0, bound - 1)
    }
    
    /**
     * Returns the next random Long value.
     */
    fun nextLong(): Long = nextBits()
    
    /**
     * Returns the next random Double value in the range [0.0, 1.0).
     */
    fun nextDouble(): Double = (nextBits() ushr 11).toDouble() / (1L shl 53)
    
    /**
     * Shuffles the given list using Fisher-Yates algorithm.
     * Returns a new list with elements in shuffled order.
     * 
     * This implementation is guaranteed to produce the same result
     * across all platforms for the same seed.
     */
    fun <T> shuffle(list: List<T>): List<T> {
        val result = list.toMutableList()
        for (i in result.size - 1 downTo 1) {
            val j = nextInt(i + 1)
            val temp = result[i]
            result[i] = result[j]
            result[j] = temp
        }
        return result
    }
    
    companion object {
        /**
         * Shuffles a list using the given seed.
         * Convenience method for one-off shuffles.
         * 
         * For any given seed, this will always produce the same result
         * regardless of platform (JVM, JS, Native).
         */
        fun <T> shuffleWithSeed(list: List<T>, seed: Long): List<T> {
            return SeededRandom(seed).shuffle(list)
        }
        
        /**
         * Generates a sequence of random integers for testing purposes.
         * Useful for verifying cross-platform consistency.
         */
        fun generateSequence(seed: Long, count: Int, bound: Int): List<Int> {
            val random = SeededRandom(seed)
            return (0 until count).map { random.nextInt(bound) }
        }
    }
}
